<template>
  <div class="kefu-iframe-modal" v-if="visible" @click="handleMaskClick">
    <!-- 移动端提示 -->
    <div class="mobile-tip" v-if="showMobileTip">{{ $t('kefu.mobileTip') }}</div>
    <div class="iframe-container" @click.stop>
      <div class="iframe-header">
        <div class="title">{{ $t('kefu.headerTitle') }}</div>
        <div class="close-btn" @click="close">
          <span class="close-icon">×</span>
        </div>
      </div>

      <!-- 加载状态 -->
      <div class="loading-container" v-if="isLoading">
        <div class="loading-spinner"></div>
        <div class="loading-text">{{ $t('kefu.connecting') }}</div>
      </div>

      <iframe
        :src="iframeUrl"
        class="kefu-iframe"
        :class="{ 'iframe-hidden': isLoading }"
        frameborder="0"
        allowfullscreen
        @load="onIframeLoad">
      </iframe>
    </div>
  </div>
</template>

<script>
import { getUser, getUserId, getRemark } from '@/utils/auth'

export default {
  name: 'KefuIframe',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    link: {
      type: String,
      default: ''
    },

    maskClosable: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      isLoading: true,
      fallbackTimer: null,
      showMobileTip: false
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.isLoading = true
        // 检查是否为移动端
        this.checkMobileDevice()
        // 添加消息监听器
        this.addMessageListener()
        // 设置备用超时机制
        this.setFallbackTimer()
      } else {
        // 移除消息监听器
        this.removeMessageListener()
        // 清除备用定时器
        this.clearFallbackTimer()
        // 隐藏移动端提示
        this.showMobileTip = false
      }
    }
  },
  mounted() {
    // 组件挂载时添加消息监听器
    this.addMessageListener()
  },
  beforeDestroy() {
    // 组件销毁前移除消息监听器
    this.removeMessageListener()
  },
  computed: {
    iframeUrl() {
      if (!this.link) return ''

      // 如果是qiabot链接，注入顾客信息后返回
      if (this.link.includes('qiabot.html')) {
        return this.enhanceQiabotUrl(this.link)
      }

      // 如果链接已经包含chatlink.html，直接使用
      if (this.link.includes('chatlink.html')) {
        // 将客服链接中的域名替换为当前访问的域名
        let kefuLink = this.link
        try {
          const currentHost = window.location.host // 获取当前域名（包含端口）
          const linkUrl = new URL(this.link)
          linkUrl.host = currentHost // 替换域名
          kefuLink = linkUrl.toString()
          console.log("linkUrl", linkUrl)
        } catch (e) {
          console.error('替换客服链接域名失败:', e)
          // 如果替换失败，使用原链接
          kefuLink = this.link
        }
        console.log("kefuLink", kefuLink)
        return this.enhanceChatLinkUrl(kefuLink)
      }

      // 否则构建新的chatlink.html URL
      return this.buildMeiqiaIframeUrl(this.link)
    }
  },
  methods: {
    // 构建美洽iframe URL
    buildMeiqiaIframeUrl(link) {
      const userId = getUserId() || ''
      const userName = getUser() || ''
      const remark = getRemark() || ''

      // 解析原始链接中的entId
      let entId = ''
      let match = link.match(/eid=([^&]+)/)
      if (match) {
        entId = match[1]
      } else {
        match = link.match(/entId=([^&]+)/)
        if (match) {
          entId = match[1]
        }
      }

      // 构建chatlink.html的URL
      const baseUrl = window.location.origin + '/chatlink.html'
      const params = new URLSearchParams({
        entId: entId,
        ID: userId,
        name: userName,
        username: userName,
        remark: remark,
        language: 'en'
      })

      return `${baseUrl}?${params.toString()}`
    },
    // 增强 qiabot.html 链接，注入顾客身份与信息
    enhanceQiabotUrl(url) {
      const userId = getUserId() || ''
      const userName = getUser() || ''
      const remark = getRemark() || ''

      const urlObj = new URL(url, window.location.origin)

      if (userId) {
        urlObj.searchParams.set('clientid', userId)
      }

      const metadata = {}
      if (userName) metadata.name = userName
      if (remark) metadata.comment = remark
      if (Object.keys(metadata).length > 0) {
        urlObj.searchParams.set('metadata', JSON.stringify(metadata))
      }

      return urlObj.toString()
    },
    // 增强已有的chatlink.html链接，添加用户信息
    enhanceChatLinkUrl(url) {
      const userId = getUserId() || ''
      const userName = getUser() || ''
      const remark = getRemark() || ''

      // 解析现有URL
      const urlObj = new URL(url, window.location.origin)

      // 添加或更新用户信息参数
      if (userId) urlObj.searchParams.set('ID', userId)
      if (userName) {
        urlObj.searchParams.set('name', userName)
        urlObj.searchParams.set('username', userName)
      }
      if (remark) urlObj.searchParams.set('remark', remark)

      // 确保有语言参数
      if (!urlObj.searchParams.has('language')) {
        urlObj.searchParams.set('language', 'en')
      }

      return urlObj.toString()
    },
    close() {
      this.$emit('close')
    },
    handleMaskClick() {
      if (this.maskClosable) {
        this.close()
      }
    },
    onIframeLoad() {
      // iframe DOM加载完成，但美洽可能还在初始化中
      // 真正的加载完成由美洽的allSet回调通知
      console.log('iframe DOM加载完成');

      // 如果3秒后还没有收到美洽回调，使用备用方案
      setTimeout(() => {
        if (this.isLoading) {
          console.log('3秒后仍在加载，使用备用方案');
          this.isLoading = false
        }
      }, 3000)
    },
    // 添加消息监听器
    addMessageListener() {
      window.addEventListener('message', this.handleMessage)
    },
    // 移除消息监听器
    removeMessageListener() {
      window.removeEventListener('message', this.handleMessage)
    },
    // 处理来自iframe的消息
    handleMessage(event) {
      if (!event.data || !event.data.type) return;

      console.log('🔔 收到美洽postMessage:', event.data);

      switch (event.data.type) {
        case 'meiqia_ready':
          console.log('✅ 美洽客服初始化完成');
          console.log('📊 消息来源:', event.data.source || 'callback');
          console.log('📊 客服在线状态:', event.data.servability);
          console.log('📊 检测到的元素:', event.data.elements || event.data.bodyChildren || event.data.element);

          // 美洽初始化完成，隐藏加载状态
          this.isLoading = false
          // 清除备用定时器
          this.clearFallbackTimer()
          break;

        case 'meiqia_conversation_start':
          console.log('✅ 美洽会话开始');
          break;

        case 'meiqia_conversation_end':
          console.log('✅ 美洽会话结束');
          break;

        case 'meiqia_smart_guide_show':
          console.log('✅ 美洽智能引导显示');
          break;

        case 'meiqia_smart_guide_close':
          console.log('✅ 美洽智能引导关闭');
          break;

        case 'meiqia_before_close':
          console.log('✅ 美洽准备关闭');
          break;

        case 'meiqia_panel_visibility':
          console.log('✅ 美洽面板可见性变化:', event.data.visibility);
          break;

        case 'meiqia_conv_click':
          console.log('✅ 美洽对话点击，类型:', event.data.convType, '对话ID:', event.data.convId);
          break;

        default:
          console.log('🔍 未知的美洽消息类型:', event.data.type);
      }
    },
    // 设置备用超时机制
    setFallbackTimer() {
      this.clearFallbackTimer()
      this.fallbackTimer = setTimeout(() => {
        console.log('美洽回调超时，使用备用方案隐藏加载状态');
        this.isLoading = false
      }, 10000) // 10秒超时
    },
    // 清除备用定时器
    clearFallbackTimer() {
      if (this.fallbackTimer) {
        clearTimeout(this.fallbackTimer)
        this.fallbackTimer = null
      }
    },
    // 检查是否为移动端设备
    checkMobileDevice() {
      const isMobile = window.innerWidth <= 767
      if (isMobile) {
        this.showMobileTip = true
        // 3秒后自动隐藏提示
        setTimeout(() => {
          this.showMobileTip = false
        }, 3000)
      }
    }
  }
}
</script>

<style lang="less" scoped>
.kefu-iframe-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.iframe-container {
  width: 95%;
  height: 90%;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  position: relative;
}

.iframe-header {
  height: 36px;
  background: #1890ff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
}

.title {
  font-size: 14px;
  font-weight: 600;
  color: #fff;
}

.close-btn {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  &:hover {
    background: #fff;
    transform: scale(1.15);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  }

  &:active {
    transform: scale(0.9);
  }

  .close-icon {
    color: #666;
    font-size: 20px;
    font-weight: bold;
    line-height: 1;
    font-family: Arial, sans-serif;
  }
}

.loading-container {
  position: absolute;
  top: 36px; // 头部高度
  left: 0;
  width: 100%;
  height: calc(100% - 36px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #fff;
  z-index: 5;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 2px solid rgba(24, 144, 255, 0.3);
  border-top: 2px solid #1890ff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}

.loading-text {
  color: #666;
  font-size: 14px;
  font-weight: 500;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.kefu-iframe {
  flex: 1;
  width: 100%;
  border: none;
  background: #fff;
  transition: opacity 0.3s ease;

  &.iframe-hidden {
    opacity: 0;
  }
}

// PC端适配
@media (min-width: 768px) {
  .iframe-container {
    width: 80%;
    height: 80%;
    max-width: 800px;
    max-height: 600px;
  }

  .iframe-header {
    height: 40px;
    padding: 0 16px;
  }

  .title {
    font-size: 16px;
  }

  .close-btn {
    width: 32px;
    height: 32px;

    .close-icon {
      font-size: 22px;
    }
  }

  .loading-container {
    top: 40px; // PC端头部高度
    height: calc(100% - 40px);
  }

  .loading-spinner {
    width: 40px;
    height: 40px;
    border-width: 3px;
    margin-bottom: 16px;
  }

  .loading-text {
    font-size: 16px;
  }
}

// 移动端提示：建议使用全屏页面
@media (max-width: 767px) {
  .kefu-iframe-modal {
    // 移动端提示信息通过 JavaScript 动态设置
    .mobile-tip {
      position: absolute;
      top: 10px;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 8px 12px;
      border-radius: 4px;
      font-size: 12px;
      z-index: 10001;
      white-space: nowrap;
    }
  }
}
</style>
